import { Inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { IFeathersAppProvider } from '../../shared/services/contracts/sync/feathers-app-provider';
import { EnvironmentService } from '../../shared/services/environment/environment.service';
import { FeathersService } from '../../shared/services/feathers.service';
import { describeError, logStep } from './session-log';

/** Result of authenticating the socket connection; the `SessionController` decides what each kind means. */
export type SocketAuthResult =
  | { kind: 'ok'; user: unknown; accessToken: string }
  /** the backend did not accept the token (401) */
  | { kind: 'refused'; reason: string }
  /** transient: timeout, 5xx, socket gone — retried inside this service already */
  | { kind: 'unavailable'; reason: string };

/** Transient backend failures that justify retrying the socket authentication. */
const TRANSIENT_ERROR_CODES = new Set([408, 502, 503, 504]);
const MAX_TRANSIENT_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 1000;

/**
 * Authenticates the socket connection to the backend and notices when that authentication is lost.
 *
 * The gateway keeps the access token on the socket connection and verifies it on every call. The
 * only way to give the connection a (new) token is `authenticate()`, and a fresh connection after a
 * reconnect has none. This class is the session's adapter to that Feathers API: it sends the
 * token, retries what is worth retrying, turns the outcome into a `SocketAuthResult`, and reports
 * a 401 on a regular data call as "the connection lost its token". It never decides what a
 * failure means for the session — that is the `SessionController`, its only caller.
 *
 * The Feathers client's own reconnect re-authentication is switched off (see `FeathersService`),
 * so nothing but this class authenticates the connection.
 */
@Injectable({ providedIn: 'root' })
export class BackendSocketAuthenticationService {
  private readonly _refused$ = new Subject<void>();
  private _hooksRegistered = false;

  /**
   * Emits when a regular data call failed with 401 / `not-authenticated`: the connection's token
   * expired or the gateway restarted. The `SessionController` subscribes once and answers with a
   * re-evaluation, which re-authenticates the connection with a fresh token.
   */
  public readonly refused$ = this._refused$.asObservable();

  constructor(
    @Inject(FeathersService) private readonly _feathers: IFeathersAppProvider,
    private readonly _environmentService: EnvironmentService
  ) {}

  /**
   * Hands the access token to the socket connection with the `fusionAuthJwt` strategy.
   *
   * Called by the `SessionController` whenever the connection needs a token: on every socket
   * `connected` (start and reconnects), after a scheduled token refresh, after a refusal with a
   * forced refresh, and on a backend refusal reported through `refused$`. Waits for the Feathers
   * app to exist, since the socket reports `connected` a moment before the app is set up on it.
   *
   * Never throws. Transient failures (timeout, 408, 502–504) are retried twice with backoff;
   * the result is one of:
   * - `ok` with the backend's user object, which the controller stores as the account;
   * - `refused` for 401 / `not-authenticated` — the token was not accepted, no retry;
   * - `unavailable` for everything else, including retries exhausted.
   */
  public async authenticate(accessToken: string): Promise<SocketAuthResult> {
    await this._environmentService.ready;
    // the socket reports `connected` a moment before the Feathers app is set up on it
    await this._feathers.ready;
    this.registerRefusalHook();

    let lastError: unknown;
    for (let attempt = 0; attempt <= MAX_TRANSIENT_RETRIES; attempt++) {
      if (attempt > 0) {
        await this.waitBeforeRetry(attempt);
        logStep('socket auth', `retry ${attempt} after a transient error: ${describeError(lastError)}`);
      }
      try {
        const result = await this._feathers.app.authenticate({
          strategy: 'fusionAuthJwt',
          accessToken,
          applicationId: this._environmentService.authConfig.clientId,
        });
        return { kind: 'ok', user: result?.user, accessToken: result?.accessToken ?? accessToken };
      } catch (error) {
        if (isRefusal(error)) {
          return { kind: 'refused', reason: describeError(error) };
        }
        if (!isTransient(error)) {
          return { kind: 'unavailable', reason: describeError(error) };
        }
        lastError = error;
      }
    }
    return {
      kind: 'unavailable',
      reason: `still failing after ${MAX_TRANSIENT_RETRIES} retries: ${describeError(lastError)}`,
    };
  }

  /**
   * Tells the backend the session is over and forgets the token on the connection.
   *
   * Called by the `SessionController` during `logout()`, and only while the socket is connected;
   * without a backend there is nothing to tell. A failure is logged by the controller and does
   * not stop the logout.
   */
  public async logout(): Promise<void> {
    await this._feathers.app.logout();
  }

  /** Exponential backoff between the retries of `authenticate()`: 1 s, then 2 s. Overridden in specs. */
  protected waitBeforeRetry(attempt: number): Promise<void> {
    const delay = RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
    return new Promise(resolve => setTimeout(resolve, delay));
  }

  /**
   * Installs a Feathers error hook, once per process, that feeds `refused$`.
   *
   * A 401 on a regular data call means the connection lost its token (expired on the socket, or
   * the gateway restarted). Without this hook the app would fail silently until the next
   * reconnect. Errors from the `authentication` service itself are excluded — those are the
   * outcome of `authenticate()` and are handled there.
   */
  private registerRefusalHook(): void {
    if (this._hooksRegistered) {
      return;
    }
    const app = this._feathers.app as { hooks?: (hooks: unknown) => void };
    if (typeof app?.hooks !== 'function') {
      return;
    }
    this._hooksRegistered = true;
    app.hooks({
      error: {
        all: [
          (context: { path?: string; error?: unknown }) => {
            if (context?.path !== 'authentication' && isRefusal(context?.error)) {
              this._refused$.next();
            }
            return context;
          },
        ],
      },
    });
  }
}

/** A Feathers `NotAuthenticated`: the backend did not accept the token. */
function isRefusal(error: unknown): boolean {
  const candidate = error as { code?: number; className?: string };
  return candidate?.code === 401 || candidate?.className === 'not-authenticated';
}

/** A failure that says nothing about the token and may pass on the next attempt. */
function isTransient(error: unknown): boolean {
  const candidate = error as { code?: number; className?: string };
  return candidate?.className === 'timeout' || (candidate?.code != null && TRANSIENT_ERROR_CODES.has(candidate.code));
}
